import { ReasonDefinition } from '@modules/taxonomy/store/types';
import {
  AddCircleRounded,
  AutoAwesomeRounded,
  AutoFixHighRounded,
  ChatRounded,
  CloseRounded,
} from '@mui/icons-material';
import {
  IconButton,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
} from '@mui/material';
import { purple } from '@mui/material/colors';
import { useGetUser } from 'v2/core/services/user';

interface ReasonDefinitionListItemProps {
  definition: ReasonDefinition;
  onRemoveDefinition?: () => void;
  onAddDefinition?: () => void;
}

export const ReasonDefinitionListItem = ({
  definition,
  onRemoveDefinition,
  onAddDefinition,
}: ReasonDefinitionListItemProps) => {
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();

  const isSearchedOrUnassignedRC =
    definition.__typename === 'UnassignedRC' ||
    definition.__typename === 'SearchedUnassignedRc';
  const isGeneratedRC = definition.__typename === 'GeneratedRC';
  const isSuggestedRC =
    definition.__typename === 'UnassignedRC' && !definition.isSelected;

  const isSelectedUnassignedRC =
    isSearchedOrUnassignedRC && definition.isSelected;
  const hasFeedbackRecord =
    isSearchedOrUnassignedRC && definition.recordIDs.length > 0;

  const isSelectedRC = isGeneratedRC || isSelectedUnassignedRC;

  const openFeedbackRecord = () => {
    if (!hasFeedbackRecord) {
      return;
    }
    const recordID = definition.recordIDs[0];

    if (recordID) {
      window.open(`/${tenantSlug}/record/${recordID}`);
    }
  };

  const renderSecondaryAction = () => {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        {isGeneratedRC && (
          <Tooltip
            arrow
            placement="top"
            title="This Definition was generated based on your prompt."
          >
            <AutoFixHighRounded color="success" />
          </Tooltip>
        )}
        {isSuggestedRC && (
          <Tooltip
            arrow
            placement="top"
            title="Suggested Definition, based on the prompt"
          >
            <AutoAwesomeRounded color="info" />
          </Tooltip>
        )}
        {hasFeedbackRecord && (
          <Tooltip arrow placement="top" title="View Feedback">
            <IconButton
              size="small"
              color="primary"
              onClick={openFeedbackRecord}
            >
              <ChatRounded sx={{ color: purple.A200 }} />
            </IconButton>
          </Tooltip>
        )}
        {isSelectedRC ? (
          <IconButton size="small" color="primary" onClick={onRemoveDefinition}>
            <CloseRounded />
          </IconButton>
        ) : (
          <IconButton size="small" color="primary" onClick={onAddDefinition}>
            <AddCircleRounded />
          </IconButton>
        )}
      </Stack>
    );
  };

  return (
    <ListItem
      divider
      key={definition.name}
      alignItems="center"
      sx={{ py: 1.25 }}
      secondaryAction={renderSecondaryAction()}
    >
      <ListItemText
        primary={definition.name}
        primaryTypographyProps={{ paddingRight: 12 }}
      />
    </ListItem>
  );
};
